import React, { useMemo } from 'react'
import { Sparkles } from 'lucide-react'
import { Typography } from '~/components/common/Typography'
import {
  landingTaskStatusGroupClass,
  type LandingTaskStatusGroup
} from '~/components/task/LandingTaskPanelShell'
import { ProgressMessageType } from '~/protos/agents'
import { useTaskProgressMessages } from '~/hooks/task/useTaskProgressMessages'
import { useConversationUuid } from '~/hooks'
import { cm } from '~/utils'

// Caret right icon
const CaretRightIcon = () => (
  <div className='relative size-4'>
    <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M6 12L10 8L6 4'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  </div>
)

// Task Bubble component based on Figma design
interface TaskBubbleProps {
  taskId: number
  title: string
  statusLabel?: string
  statusGroup?: LandingTaskStatusGroup
  progressMessageText?: string // Optional: Latest progress message button text (if not provided, will fetch from API)
  onTaskClick?: (taskId: number) => void
  onTaskIdLinkClick?: (taskId: number) => void
  isDeleted?: boolean // Safeguard: indicates if task was deleted
  conversationUuid?: string // Optional: If provided, will fetch progress messages from API
}

// Internal component that fetches progress messages
const TaskBubbleWithProgress: React.FC<TaskBubbleProps> = ({
  taskId,
  title,
  statusLabel,
  statusGroup,
  progressMessageText: providedProgressMessageText,
  onTaskClick,
  onTaskIdLinkClick,
  isDeleted = false,
  conversationUuid
}) => {
  // Fetch progress messages for this task if conversationUuid is provided and no progress message text was provided
  const conversationUuidFromHook = useConversationUuid()
  const effectiveConversationUuid = conversationUuid || conversationUuidFromHook

  const { data: progressMessages } = useTaskProgressMessages(
    effectiveConversationUuid || '',
    String(taskId),
    {
      enabled: !providedProgressMessageText && !!effectiveConversationUuid
    }
  )

  // Get the latest non-SUMMARY progress message title
  const latestProgressMessageText = useMemo(() => {
    if (providedProgressMessageText) {
      return providedProgressMessageText
    }

    if (!progressMessages || progressMessages.length === 0) {
      return undefined
    }

    // Find the latest non-SUMMARY progress message
    // Progress messages are returned in chronological order, so we look from the end
    for (let i = progressMessages.length - 1; i >= 0; i--) {
      const msg = progressMessages[i]
      if (msg.messageType !== ProgressMessageType.SUMMARY && msg.title) {
        return msg.title
      }
    }

    // If no non-SUMMARY found, use the latest one (even if SUMMARY)
    const latest = progressMessages[progressMessages.length - 1]
    return latest?.title
  }, [providedProgressMessageText, progressMessages])

  return (
    <TaskBubbleInner
      taskId={taskId}
      title={title}
      statusLabel={statusLabel}
      statusGroup={statusGroup}
      progressMessageText={latestProgressMessageText}
      onTaskClick={onTaskClick}
      onTaskIdLinkClick={onTaskIdLinkClick}
      isDeleted={isDeleted}
    />
  )
}

// Inner component that renders the actual bubble
const TaskBubbleInner: React.FC<Omit<TaskBubbleProps, 'conversationUuid'>> = ({
  taskId,
  title,
  statusLabel,
  statusGroup,
  progressMessageText,
  onTaskClick,
  onTaskIdLinkClick,
  isDeleted = false
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Safeguard: Don't handle click if task is deleted or no handler provided
    if (isDeleted || !onTaskClick) {
      return
    }
    onTaskClick(taskId)
  }

  const handleTaskIdLinkClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (isDeleted || !onTaskIdLinkClick) {
      return
    }
    onTaskIdLinkClick(taskId)
  }

  // Safeguard: Ensure taskId and title are valid
  if (!taskId || !title) {
    return null
  }

  const taskIdLabel = (
    <Typography
      fontFamily='geist'
      fontSize='bodySmMedium'
      fontWeight='medium'
      className='text-inherit'
    >
      #{taskId}
    </Typography>
  )

  return (
    <div
      className={`flex w-[calc(100%_-_32px)] max-w-full flex-col justify-center gap-2 rounded-3xl border border-tokens-neutral-bg-white bg-tokens-neutral-bg-white/50 p-4 transition-[background-color,box-shadow] duration-200 ${
        isDeleted
          ? 'cursor-not-allowed opacity-60'
          : 'cursor-pointer hover:bg-tokens-neutral-bg-white hover:shadow-segment'
      }`}
      onClick={handleClick}
    >
      <div className='flex items-center justify-between self-stretch'>
        <div className='flex min-w-0 items-center gap-4'>
          <div className='flex items-center p-1'>
            <div className='flex items-center rounded-lg bg-tokens-status-link-base/10 p-1 text-tokens-status-link-3'>
              <div className='flex size-6 items-center justify-center'>
                <Sparkles className='size-[18px]' />
              </div>
            </div>
          </div>
          <div className='flex min-w-0 flex-col'>
            {/* Task Number */}
            {onTaskIdLinkClick && !isDeleted ? (
              <button
                type='button'
                onClick={handleTaskIdLinkClick}
                className='w-fit cursor-pointer rounded-[4px] border-0 bg-transparent p-0 text-left text-tokens-status-link-3 transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tokens-status-link-2 focus-visible:ring-offset-2'
                aria-label={`Open task #${taskId}`}
              >
                {taskIdLabel}
              </button>
            ) : (
              <span className='inline-flex w-fit text-tokens-neutral-text-tertiary'>
                {taskIdLabel}
              </span>
            )}
            {/* Task Title */}
            <Typography
              fontFamily='geist'
              fontSize='bodyMdMedium'
              fontWeight='medium'
              className='truncate text-tokens-neutral-text-primary'
            >
              {title}
            </Typography>
          </div>
        </div>
        <div className='ml-3 flex shrink-0 items-center gap-2'>
          {statusLabel ? (
            <div
              data-testid='task-status-pill'
              className={cm(
                'max-w-[180px] truncate rounded-full border px-2.5 py-1 text-xs font-semibold leading-none',
                landingTaskStatusGroupClass(statusGroup)
              )}
              title={statusLabel}
            >
              {statusLabel}
            </div>
          ) : null}
          <div className='flex flex-col items-center justify-center overflow-hidden rounded-xl'>
            <div className='flex items-center justify-center p-1.5 text-tokens-primary-2'>
              <CaretRightIcon />
            </div>
          </div>
        </div>
      </div>

      {/* Horizontal Divider */}
      <div className='h-px w-full bg-tokens-neutral-border-2' />

      {/* Progress Message Text, Deleted Placeholder, or Default Placeholder */}
      <div className='flex w-full min-w-0 items-center justify-between self-stretch pl-[8px] font-normal'>
        <Typography
          fontFamily='geistMono'
          fontSize='bodySmMedium'
          fontWeight='semibold'
          className={`truncate text-tokens-neutral-text-tertiary ${!progressMessageText && 'italic'} leading-normal`}
        >
          {isDeleted ? 'Task deleted' : progressMessageText || 'No progress available'}
        </Typography>
      </div>
    </div>
  )
}

// Export the component that fetches progress messages
export const TaskBubble: React.FC<TaskBubbleProps> = props => {
  // If progressMessageText is provided, use it directly (for backward compatibility)
  // Otherwise, fetch from API
  if (props.progressMessageText !== undefined) {
    return <TaskBubbleInner {...props} />
  }
  return <TaskBubbleWithProgress {...props} />
}

export default TaskBubble
